import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeftRight, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Empty, PageHeader, Spinner, StatusBadge } from "@/components/kit";
import { SettlementRow } from "@/components/Rows";
import { fdate, firstName, inr } from "@/lib/format";

function FundingItem({ t }) {
  const receipt = ["sell", "partial_exit", "complete_exit"].includes(t.type);
  return (
    <Link to={`/txn/${t.id}`} className="hd-card block hover:border-[#C8553D]/40 transition-colors" data-testid={`ledger-funding-${t.id}`}>
      <div className="flex justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{t.type_label} · {t.asset_name}</div>
          <div className="text-xs text-[#718096]">{fdate(t.date)}</div>
        </div>
        <div className="text-right"><div className="font-bold tabular-nums">{inr(t.total_value)}</div><StatusBadge status={t.status} /></div>
      </div>
      <div className="text-xs text-[#4A5568] mt-2">{receipt ? "Received by" : "Paid by"}: {t.payers.map((p) => `${p.full_name} ${inr(p.amount)}`).join(", ") || "—"}</div>
      <div className="grid grid-cols-2 gap-2 mt-2">
        {t.side_breakdown.map((s) => (
          <div key={s.karta_id} className="rounded-xl bg-[#F5F0E9] px-3 py-2 text-xs">
            <div className="font-semibold">{firstName(s.name)} Side</div>
            <div className="text-[#718096]">Share {inr(s.expected)} · {receipt ? "got" : "paid"} {inr(s.actual)}</div>
            {Number(s.net) !== 0 && <div className={`font-bold ${Number(s.net) > 0 ? "text-emerald-700" : "text-[#B34832]"}`}>{Number(s.net) > 0 ? "Owed " : "Owes "}{inr(Math.abs(Number(s.net)))}</div>}
          </div>
        ))}
      </div>
    </Link>
  );
}

export default function Ledger() {
  const { gid, person } = useAuth();
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("funding");
  useEffect(() => {
    api.get(`/groups/${gid}/ledger`).then((r) => setD(r.data));
  }, [gid]);
  if (!d) return <Spinner />;
  const p = d.position;
  const other = p.sides.find((s) => s.karta_id !== person.id);
  const settleLink = p.summary.direction === "you_owe" ? `/add/settlement?from=${person.id}&to=${other?.karta_id}&amount=${p.summary.amount}`
    : p.summary.direction === "owed_to_you" ? `/add/settlement?from=${other?.karta_id}&to=${person.id}&amount=${p.summary.amount}` : "/add/settlement";
  return (
    <div>
      <PageHeader title="Ledger" subtitle="Contributions, funding & settlements" back={false} />
      <div className="rounded-3xl bg-[#1A202C] text-white p-5" data-testid="ledger-position-card">
        <div className="text-sm text-white/70">Settlement position</div>
        <div className="text-2xl font-bold mt-1" data-testid="ledger-position-summary">
          {p.summary.direction === "settled" ? "All settled" : `${p.summary.text} ${inr(p.summary.amount)}`}
        </div>
        <div className="mt-4 space-y-1.5">
          {p.sides.map((s) => (
            <div key={s.karta_id} className="flex justify-between text-sm">
              <span className="text-white/80">{firstName(s.name)} Side</span>
              <span className={`font-semibold tabular-nums ${Number(s.balance) > 0 ? "text-emerald-300" : Number(s.balance) < 0 ? "text-orange-300" : ""}`} data-testid={`ledger-side-balance-${firstName(s.name)}`}>
                {Number(s.balance) > 0 ? "is owed " : Number(s.balance) < 0 ? "owes " : ""}{inr(Math.abs(Number(s.balance)))}
              </span>
            </div>
          ))}
        </div>
        <Link to={settleLink} className="hd-btn mt-4 w-full bg-white text-[#1A202C] h-11 text-sm" data-testid="ledger-record-settlement"><ArrowLeftRight size={16} /> Record settlement</Link>
      </div>

      {p.persons.length > 0 && (
        <div className="hd-card mt-3" data-testid="ledger-person-balances">
          <div className="hd-label mb-1">By person (underlying owners)</div>
          {p.persons.map((x) => (
            <div key={x.person_id} className="flex justify-between py-2 text-sm border-b border-[#E2D9CF]/50 last:border-0">
              <span><span className="font-semibold">{x.full_name}</span> <span className="hs-id text-[11px] text-[#718096]">{x.hs_id}</span></span>
              <span className={`font-semibold tabular-nums ${Number(x.balance) > 0 ? "text-emerald-700" : "text-[#B34832]"}`}>{Number(x.balance) > 0 ? "is owed " : "owes "}{inr(Math.abs(Number(x.balance)))}</span>
            </div>
          ))}
          <div className="text-xs text-[#718096] mt-1">Each person’s paid amount minus their ownership share, after settlements.</div>
        </div>
      )}

      <div className="flex bg-white rounded-2xl p-1 border border-[#E2D9CF]/60 mt-5">
        {[["funding", "Funding"], ["settlements", "Settlements"]].map(([id, l]) => (
          <button key={id} data-testid={`ledger-tab-${id}`} onClick={() => setTab(id)} className={`flex-1 h-10 rounded-xl text-sm font-semibold transition-colors ${tab === id ? "bg-[#C8553D] text-white" : "text-[#4A5568]"}`}>{l}</button>
        ))}
      </div>
      <div className="mt-4 space-y-3">
        {tab === "funding" && (d.funding.length ? d.funding.map((t) => <FundingItem key={t.id} t={t} />) : <Empty icon={Receipt} text="No contributions yet" testid="ledger-funding-empty" />)}
        {tab === "settlements" && (d.settlements.length ? <div className="hd-card py-1">{d.settlements.map((s) => <SettlementRow key={s.id} s={s} />)}</div> : <Empty icon={ArrowLeftRight} text="No settlements yet" testid="ledger-settlements-empty" />)}
      </div>
    </div>
  );
}
